import apiClient, { getErrorMessage } from './api.service';
import { API_ENDPOINTS } from '../config/api';

export interface Bed {
  bedNumber: number;
  isOccupied: boolean;
  currentBookingId?: {
    _id: string;
    userId?: {
      _id: string;
      gender?: string;
    };
  } | null;
}

export interface Room {
  _id: string;
  roomNumber: number;
  floor: number;
  roomType: string;
  totalBeds: number;
  availableBeds: number;
  beds: Bed[];
  status: 'active' | 'maintenance' | 'inactive';
}

export interface RoomsResponse {
  status: string;
  count: number;
  data: {
    rooms: Room[];
  };
}

export interface RoomResponse {
  status: string;
  data: {
    room: Room;
  };
}

export interface BedAvailabilityResponse {
  status: string;
  data: {
    roomNumber: number;
    bedNumber: number;
    isAvailable: boolean;
  };
}

class RoomService {
  async getAllRooms(): Promise<Room[]> {
    try {
      const response = await apiClient.get<RoomsResponse>(API_ENDPOINTS.ROOMS.GET_ALL);
      return response.data.data.rooms;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async getAvailableRooms(): Promise<Room[]> {
    try {
      const response = await apiClient.get<RoomsResponse>(API_ENDPOINTS.ROOMS.GET_AVAILABLE);
      return response.data.data.rooms;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async getRoomByNumber(roomNumber: number): Promise<Room> {
    try {
      const response = await apiClient.get<RoomResponse>(
        API_ENDPOINTS.ROOMS.GET_BY_NUMBER(roomNumber)
      );
      return response.data.data.room;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async checkBedAvailability(roomNumber: number, bedNumber: number): Promise<boolean> {
    try {
      const response = await apiClient.get<BedAvailabilityResponse>(
        API_ENDPOINTS.ROOMS.CHECK_AVAILABILITY(roomNumber, bedNumber)
      );
      return response.data.data.isAvailable;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  // Admin: open/close a room by updating its status (active | maintenance | inactive)
  async updateRoomStatus(roomId: string, status: Room['status']): Promise<Room> {
    try {
      const response = await apiClient.patch<RoomResponse>(
        API_ENDPOINTS.ADMIN.UPDATE_ROOM_STATUS(roomId),
        { status }
      );
      return response.data.data.room;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }
}

const roomService = new RoomService();
export default roomService;
